import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

export type OtsPaginationProps = {
  /** One-based current page. */
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
  compact?: boolean;
  itemLabel?: string;
  className?: string;
};

const getPages = (
  page: number,
  totalPages: number
): Array<number | 'ellipsis-left' | 'ellipsis-right'> => {
  if (totalPages <= 7)
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  const pages: Array<number | 'ellipsis-left' | 'ellipsis-right'> = [1];
  if (page > 4) pages.push('ellipsis-left');
  for (
    let value = Math.max(2, page - 1);
    value <= Math.min(totalPages - 1, page + 1);
    value += 1
  )
    pages.push(value);
  if (page < totalPages - 3) pages.push('ellipsis-right');
  pages.push(totalPages);
  return pages;
};

/** Shared controlled pagination for API-backed tables. Page values are intentionally one-based for UI callers. */
export const OtsPagination = ({
  page,
  pageSize,
  totalItems,
  onPageChange,
  disabled = false,
  compact = false,
  itemLabel = 'items',
  className,
}: OtsPaginationProps) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / Math.max(1, pageSize)));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const firstItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <nav
      aria-label={`${itemLabel} pagination`}
      className={[
        'flex flex-wrap items-center justify-between gap-3',
        compact ? 'text-xs' : 'text-sm',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <p className="text-[var(--ots-text-muted)]">
        <span className="font-semibold tabular-nums text-[var(--ots-secondary)]">
          {firstItem}–{lastItem}
        </span>{' '}
        of{' '}
        <span className="font-semibold tabular-nums text-[var(--ots-secondary)]">
          {totalItems}
        </span>{' '}
        {itemLabel}
      </p>
      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label="Previous page"
          disabled={disabled || currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
          className="inline-flex size-8 items-center justify-center rounded-md border border-[var(--ots-outline-variant)] text-[var(--ots-primary)] hover:bg-[var(--ots-background)] disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeftIcon className="size-4" />
        </button>
        {!compact &&
          getPages(currentPage, totalPages).map((value) =>
            typeof value === 'number' ? (
              <button
                key={value}
                type="button"
                aria-label={`Page ${value}`}
                aria-current={value === currentPage ? 'page' : undefined}
                disabled={disabled}
                onClick={() => onPageChange(value)}
                className={[
                  'inline-flex size-8 items-center justify-center rounded-md border text-xs font-semibold tabular-nums',
                  value === currentPage
                    ? 'border-[var(--ots-primary)] bg-[var(--ots-primary)] text-[var(--ots-on-primary)]'
                    : 'border-[var(--ots-outline-variant)] text-[var(--ots-primary)] hover:bg-[var(--ots-background)]',
                ].join(' ')}
              >
                {value}
              </button>
            ) : (
              <span
                key={value}
                className="w-6 text-center text-[var(--ots-text-muted)]"
              >
                …
              </span>
            )
          )}
        {compact && (
          <span className="px-2 font-semibold tabular-nums text-[var(--ots-secondary)]">
            {currentPage} / {totalPages}
          </span>
        )}
        <button
          type="button"
          aria-label="Next page"
          disabled={disabled || currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          className="inline-flex size-8 items-center justify-center rounded-md border border-[var(--ots-outline-variant)] text-[var(--ots-primary)] hover:bg-[var(--ots-background)] disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronRightIcon className="size-4" />
        </button>
      </div>
    </nav>
  );
};
